import { useState, useRef } from "react";
import { Form, Button, Card, Spinner } from "react-bootstrap";
import { BsTrash } from "react-icons/bs";
import { useParams } from "react-router-dom";
import { uploadDocumentRequest, uploadDocumentFilesRequest, deleteDocumentRequest } from "@/services";
import { formatApiErrorMessage } from "@/services/_apiConfig";
import DocumentFiles from "../document_files";
import { uploadEachFile, describeUploadFailures } from "../upload_utils";

const BuyerInvoicesUploadForm = ({
  doc,
  documentsList = [],
  reloadDocumentsList,
  required = false,
  validationError,
  onUploadingChange,
}) => {
  const { applicationId: applicationUuid } = useParams();
  const [adding, setAdding] = useState(false);
  const [buyerName, setBuyerName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [uploadingId, setUploadingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [cardError, setCardError] = useState({ id: null, message: "" });
  const fileInputsRef = useRef({});

  const buyers = documentsList.filter((d) => d.document_type === doc.document_type);

  const handleCancel = () => {
    setAdding(false);
    setBuyerName("");
    setError("");
  };

  const handleSaveBuyer = async () => {
    const name = buyerName.trim();
    if (!name) {
      setError("Please enter the buyer name.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append("loan_id", applicationUuid);
      formData.append("document_type_id", doc.document_type);
      formData.append("metadata", JSON.stringify({ buyer_name: name }));
      const res = await uploadDocumentRequest(formData);
      if (res?.success === false) {
        setError(formatApiErrorMessage(res.error, "Error saving buyer. Try again."));
        return;
      }
      await reloadDocumentsList();
      handleCancel();
    } catch (err) {
      setError(err?.message || "Failed to save buyer. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleUploadFiles = async (e, buyerDoc) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setUploadingId(buyerDoc.id);
    onUploadingChange?.(true);
    setCardError({ id: null, message: "" });
    try {
      const failures = await uploadEachFile(files, (file) => {
        const uploadData = new FormData();
        uploadData.append("file", file);
        return uploadDocumentFilesRequest(buyerDoc.id, uploadData);
      });
      setCardError({ id: buyerDoc.id, message: describeUploadFailures(failures, files.length) });
      await reloadDocumentsList();
    } catch (err) {
      setCardError({ id: buyerDoc.id, message: err?.message || "File upload failed. Try again." });
    } finally {
      if (fileInputsRef.current[buyerDoc.id]) fileInputsRef.current[buyerDoc.id].value = "";
      setUploadingId(null);
      onUploadingChange?.(false);
    }
  };

  const handleDeleteBuyer = async (buyerDoc) => {
    const name = buyerDoc.metadata?.buyer_name || "this buyer";
    if (!window.confirm(`Delete ${name}? This will remove all invoice copies too.`)) return;

    setDeletingId(buyerDoc.id);
    try {
      const res = await deleteDocumentRequest(buyerDoc.uuid);
      if (res?.success === false) {
        setCardError({ id: buyerDoc.id, message: "Failed to delete buyer. Try again." });
        return;
      }
      await reloadDocumentsList();
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="mb-4 border rounded p-3">
      <h6>
        {doc.title} {required && <span className="text-danger">*</span>}
      </h6>
      {doc.description && <p className="text-muted">{doc.description}</p>}
      {validationError && (
        <p className="text-danger mb-2" role="alert">{validationError}</p>
      )}

      {buyers.length > 0 ? (
        buyers.map((buyerDoc) => (
          <Card key={buyerDoc.id} className="mb-3 shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-1">{buyerDoc.metadata?.buyer_name || "Others"}</h6>
                <Button
                  variant="link"
                  className="p-0 text-danger"
                  title="Delete buyer and all invoice copies"
                  onClick={() => handleDeleteBuyer(buyerDoc)}
                  disabled={deletingId === buyerDoc.id}
                >
                  {deletingId === buyerDoc.id ? <Spinner animation="border" size="sm" /> : <BsTrash size={18} />}
                </Button>
              </div>

              <Form.Control
                className="file-input--hide-chosen-text"
                type="file"
                aria-label={`Upload invoice copies for ${buyerDoc.metadata?.buyer_name || "buyer"}`}
                multiple
                onChange={(e) => handleUploadFiles(e, buyerDoc)}
                ref={(el) => (fileInputsRef.current[buyerDoc.id] = el)}
                disabled={!!uploadingId}
                data-testid="fincobox-upload-buyer-file-input"
              />
              {uploadingId === buyerDoc.id && (
                <div className="d-flex justify-content-center align-items-center mt-2 text-muted" role="status">
                  <Spinner animation="border" size="sm" />
                  <span className="ms-2">Uploading file...</span>
                </div>
              )}
              {cardError.id === buyerDoc.id && cardError.message && (
                <p className="text-danger small mt-2 mb-0" role="alert">{cardError.message}</p>
              )}
              <div className="mt-2">
                <DocumentFiles files={buyerDoc.document_files || []} reloadDocumentsList={reloadDocumentsList} />
              </div>
            </Card.Body>
          </Card>
        ))
      ) : (
        <p className="text-muted">No buyer added yet.</p>
      )}

      {adding ? (
        <Card className="mt-3 p-3 shadow-sm">
          <Form.Group className="mb-3" controlId="add-buyer-name">
            <Form.Label>Name of Buyer</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter buyer name"
              value={buyerName}
              onChange={(e) => setBuyerName(e.target.value)}
              data-testid="fincobox-upload-buyer-name"
            />
          </Form.Group>
          {error && <p className="text-danger small">{error}</p>}
          <div className="d-flex justify-content-end gap-2">
            <Button variant="secondary" onClick={handleCancel}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSaveBuyer} disabled={saving} data-testid="fincobox-upload-save-buyer">
              {saving ? <Spinner animation="border" size="sm" /> : "Save"}
            </Button>
          </div>
        </Card>
      ) : (
        <Button variant="outline-primary" onClick={() => setAdding(true)} data-testid="fincobox-upload-add-buyer">
          + Add Buyer
        </Button>
      )}
    </div>
  );
};

export default BuyerInvoicesUploadForm;
